import { ServiceCard, ServiceCardSkeleton } from "@/components/ServiceCard";
import { AdaptiveServiceCard } from "@/components/AdaptiveServiceCard";
import { useQueryClient } from "@tanstack/react-query";
import { memo, useEffect, useCallback, useRef, useState } from "react";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious, PaginationEllipsis } from "@/components/ui/pagination";
import { isLowDataMode } from "@/utils/networkMonitor";
import { useIsMobile } from "@/hooks/use-mobile";
import { useDisplaySettings } from "@/hooks/useDisplaySettings";
import { validateServiceData, safeString, safeNumber } from "@/utils/reactSafety";
import { MobileErrorBoundary } from "@/components/ui/mobile-error-boundary";

interface ServiceListProps {
  loading: boolean;
  services: any[];
  activeCategory: string;
  activeSubcategoryId: number | null;
  searchQuery: string;
  totalPages?: number;
  currentPage?: number;
  onPageChange?: (page: number) => void;
  connectionStatus?: "slow" | "fast";
}

export const ServiceList = memo(({
  loading,
  services,
  activeCategory,
  activeSubcategoryId,
  searchQuery,
  totalPages = 1,
  currentPage = 1,
  onPageChange = () => {},
  connectionStatus = "fast"
}: ServiceListProps) => {
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const isSlowConnectionRef = useRef(connectionStatus === "slow" || isLowDataMode());
  const prefetchAttemptedRef = useRef(false);
  const { layoutMode } = useDisplaySettings();
  
  // Only prefetch next page data in select cases to reduce network usage
  useEffect(() => {
    // Skip if already prefetched, at the last page, or on slow connection
    if (prefetchAttemptedRef.current || currentPage >= totalPages || isSlowConnectionRef.current) {
      return;
    }
    
    const timer = setTimeout(() => {
      const nextPage = currentPage + 1;
      
      // Mark that we've attempted to prefetch
      prefetchAttemptedRef.current = true;
      
      // Check if already in cache
      const queryKey = ['services', searchQuery, activeSubcategoryId, nextPage];
      const existingData = queryClient.getQueryData(queryKey);
      
      if (!existingData) {
        queryClient.prefetchQuery({
          queryKey: queryKey,
          staleTime: 2 * 60 * 1000, // 2 minutes
        });
      }
    }, 3000); // Longer delay to prioritize visible content
    
    return () => clearTimeout(timer);
  }, [currentPage, totalPages, queryClient, searchQuery, activeSubcategoryId]);
  
  // Reset prefetch flag when page changes
  useEffect(() => {
    prefetchAttemptedRef.current = false;
  }, [currentPage]);
  
  if (loading) {
    // Reduced number of skeleton items for slow connections
    const skeletonCount = isSlowConnectionRef.current ? 4 : 8;
    
    return (
      <div className="space-y-6">
        <div className={layoutMode === 'adaptive' 
          ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 auto-rows-max"
          : "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
        }>
          {[...Array(skeletonCount)].map((_, index) => (
            <ServiceCardSkeleton key={index} optimized={isSlowConnectionRef.current} />
          ))}
        </div>
      </div>
    );
  }

  if (services.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        {activeSubcategoryId
          ? `No services found in ${activeCategory}`
          : searchQuery 
            ? "No services found matching your search"
            : "No featured services available at the moment"}
      </div>
    );
  }

  const getPageNumbers = () => {
    const pageNumbers = [];
    // For slow connections, show fewer pagination items
    const maxPagesToShow = isSlowConnectionRef.current ? 3 : 5;
    
    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      pageNumbers.push(1);
      
      let startPage = Math.max(2, currentPage - 1);
      const endPage = Math.min(totalPages - 1, startPage + (isSlowConnectionRef.current ? 0 : 2));
      
      startPage = Math.max(2, endPage - (isSlowConnectionRef.current ? 0 : 2));
      
      if (startPage > 2) {
        pageNumbers.push('ellipsis-start');
      }
      
      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }
      
      if (endPage < totalPages - 1) {
        pageNumbers.push('ellipsis-end');
      }
      
      pageNumbers.push(totalPages);
    }
    
    return pageNumbers;
  };

  return (
    <div className="space-y-6">
      {/* Service Grid */}
      <div className={layoutMode === 'adaptive' 
        ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 auto-rows-max"
        : "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
      }>
        {services.map((service) => {
          // Validate service data before rendering
          if (!validateServiceData(service)) {
            console.error('Skipping invalid service:', service);
            return null;
          }

          // Additional safety check for subcategory_details to prevent React #31 errors
          const safeSubcategoryDetails = service.subcategory_details && typeof service.subcategory_details === 'object' 
            ? {
                label: typeof service.subcategory_details.label === 'string' ? service.subcategory_details.label : null,
                parent: service.subcategory_details.parent && typeof service.subcategory_details.parent === 'object'
                  ? {
                      label: typeof service.subcategory_details.parent.label === 'string' ? service.subcategory_details.parent.label : null
                    }
                  : null
              }
            : null;

          return (
            <MobileErrorBoundary key={service.id} componentName="ServiceCard">
              <div className="h-full">
                {layoutMode === 'adaptive' ? (
                  <AdaptiveServiceCard
                    id={safeNumber(service.id)}
                    title={safeString(service.name)}
                    subcategory_details={safeSubcategoryDetails}
                    price={safeNumber(service.price)}
                    rating={safeNumber(service.rating)}
                    image={safeString(service.image_url, 'https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d')}
                    contact={{
                      phone: safeString(service.phone),
                      email: safeString(service.email),
                      location: safeString(service.location)
                    }}
                    region={safeString(service.region, "nairobi")}
                    cardType="auto"
                  />
                ) : (
                  <ServiceCard
                    id={safeNumber(service.id)}
                    title={safeString(service.name)}
                    subcategory_details={safeSubcategoryDetails}
                    price={safeNumber(service.price)}
                    rating={safeNumber(service.rating)}
                    image={safeString(service.image_url, 'https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d')}
                    contact={{
                      phone: safeString(service.phone),
                      email: safeString(service.email),
                      location: safeString(service.location)
                    }}
                    region={safeString(service.region, "nairobi")}
                  />
                )}
              </div>
            </MobileErrorBoundary>
          );
        }).filter(Boolean)}
      </div>

      {totalPages > 1 && (
        <Pagination className="py-4">
          <PaginationContent>
            {currentPage > 1 && (
              <PaginationItem>
                <PaginationPrevious 
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    onPageChange(currentPage - 1);
                  }}
                />
              </PaginationItem>
            )}
            
            {getPageNumbers().map((page, i) => (
              page === 'ellipsis-start' || page === 'ellipsis-end' ? (
                <PaginationItem key={`ellipsis-${i}`}>
                  <PaginationEllipsis />
                </PaginationItem>
              ) : (
                <PaginationItem key={page}>
                  <PaginationLink
                    href="#"
                    isActive={page === currentPage}
                    onClick={(e) => {
                      e.preventDefault();
                      onPageChange(page as number);
                    }}
                  >
                    {page}
                  </PaginationLink>
                </PaginationItem>
              )
            ))}
            
            {currentPage < totalPages && (
              <PaginationItem>
                <PaginationNext 
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    onPageChange(currentPage + 1);
                  }}
                />
              </PaginationItem>
            )}
          </PaginationContent>
        </Pagination>
      )}
    </div>
  );
});

ServiceList.displayName = "ServiceList";
